import 'dayjs/locale/sv';

import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { ChangeEvent, useMemo, useState } from 'react';
import { ChevronLeft, Copy, Info } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Link, Spinner } from '@/components';
import { BriefViewer } from '@/components/BriefViewer';
import { useAuth, useAuthUser } from '@/hooks';
import { useCollaboration, useInviteAccept, useInviteDecline } from '@/hooks/influencer';
import { fixUrlForCloudFront } from '@/utils/cloudfront';
import { getFileIcon, PlatformIcon } from '@/utils/platformIcon';
import { updateUserInStorage } from '@/utils/auth';
import { TranslatedLink } from '@/utils/translatedComponents';

import { ExpandableBox } from '@/components/expandable-box';
import { Checkbox, CheckboxProps, Divider, message, Tooltip } from 'antd/lib';
import Footer from '@/components/footer/Footer';
import { AGREEMENT_URL } from '@/utils/links';
import IconExpired from '../../../public/assets/icons/clock_expired.svg';
import IconDeclined from '../../../public/assets/icons/declined-document.svg';
import IconNote from '../../../public/assets/icons/note.svg';

import styles from './InviteView.module.css';
import AdressModal from './modals/AdressModal';
import CampaignWelcomeModal from './modals/CampaignWelcomeModal';
import CheckAddressModal from './modals/CheckAddressModal';
import ChooseDateModal from './modals/ChooseDateModal';
import DeclineModal from './modals/DeclineModal';
import UserInfoModal from './modals/UserInfoModal';

export enum Step {
  Welcome = 'WELCOME',
  UserInfo = 'USER_INFORMATION',
  CheckAddress = 'CHECK_ADDRESS',
  ShippingAddress = 'SHIPPING_ADDRESS',
  ChooseDate = 'CHOOSE_DATE',
  Decline = 'DECLINE'
}

export interface InviteViewProps {
  collaborationId?: string;
}

export function InviteView({ collaborationId: propCollaborationId }: InviteViewProps) {
  const [rawChosenPublishDates, setRawChosenPublishDates] = useState<TypeAny[]>([]);
  const [chosenPublishDates, setChosenPublishDates] = useState<TypeAny[]>([]);
  const [checkShippingAddress, setCheckShippingAddress] = useState(false);
  const [terms, setTerms] = useState(false);
  const [reason, setReason] = useState('');
  const user = useAuthUser();
  const { authData, setAuthData } = useAuth();
  const router = useRouter();
  const { t } = useTranslation();
  const [shippingAddress, setShippingAddress] = useState<TypeAny>({
    name: user?.influencer?.shipping_address?.name,
    co: user?.influencer?.shipping_address?.co,
    state: user?.influencer?.shipping_address?.state,
    address1: user?.influencer?.shipping_address?.address1,
    postal_code: user?.influencer?.shipping_address?.postal_code,
    door_code: user?.influencer?.shipping_address?.door_code,
    city: user?.influencer?.shipping_address?.city,
    country: user?.influencer?.shipping_address?.country || user?.country
  });
  const [missingData, setMissingData] = useState<Step | null>(null);
  const [userData, setUserData] = useState<Api.UserObject>(() => user);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);

  const { id: routerCollaborationId = '' } = router?.query || {};
  const collaborationIdAsString = propCollaborationId || routerCollaborationId.toString();

  const { data, isLoading } = useCollaboration({ id: collaborationIdAsString });

  const memoData = useMemo(() => data as Api.Influencer.Collaboration, [data]);

  const { mutate: acceptInvite, isLoading: isAccepting } = useInviteAccept({
    onSuccess: (res: TypeAny) => {
      setMissingData(null);
      if ('code' in res && res.code === 'INVITE_ACCEPTED') {
        setMissingData(Step.Welcome);
      }
    },
    chosenPublishDates,
    collaborationId: collaborationIdAsString,
    shippingAddress
  });

  const { mutate: declineInvite, isLoading: isDeclining } = useInviteDecline({
    onSuccess: (response: ResponseObject) => {
      // Only redirect if there's no error code in the response
      if (!('code' in response)) {
        router.push(fixUrlForCloudFront('/connect/collaborations'));
      }
    },
    collaborationId: collaborationIdAsString,
    reason
  });

  const confirmTerms: CheckboxProps['onChange'] = (e) => {
    setTerms(e.target.checked);
  };

  if (isLoading)
    return (
      <div className={styles.AuthWrap}>
        <div className={styles.AuthSpinner}>
          <Spinner />
        </div>
      </div>
    );
  if (!memoData?.campaign) return null;

  const hasMultipleDates = memoData.beats.some((beat) => beat.dates_publish?.length > 1);
  const missingSelectedDate =
    (rawChosenPublishDates.filter((b) => !!b.publish_date).length !==
      memoData.beats.filter((b) => ['IGCP', 'ORGPOST'].includes(b.beat_type || '')).length ||
      (rawChosenPublishDates.length === memoData.beats.length &&
        rawChosenPublishDates.some((beat: TypeAny) => !beat.publish_date))) &&
    memoData.beats.some((beat) => !!beat.dates_publish?.length) &&
    memoData.beats.some((beat) => ['IGCP', 'ORGPOST'].includes(beat.beat_type || ''));
  const hasShipping = memoData.product_delivery === 'SHIPPING';
  const missingAddress =
    !shippingAddress.address1 ||
    !shippingAddress.country ||
    !shippingAddress.postal_code ||
    !shippingAddress.city;

  function formattChosenPublishDates() {
    return rawChosenPublishDates.map((d) => {
      const formattedDateValue = dayjs(d.publish_date.value).format('YYYY-MM-DD');
      const combinedDate = `${formattedDateValue}T${d.publish_time}`;
      const formattedDatetime = dayjs(combinedDate).format();
      return {
        ...d,
        publish_date: d.publish_date.value,
        date_publish: formattedDatetime
      };
    });
  }
  function handleAcceptInvite() {
    const formattedData = formattChosenPublishDates();
    setChosenPublishDates(formattedData);
    acceptInvite();
  }
  // function handleUpdateInvite() {
  //   const formattedData = formattChosenPublishDates();
  //   setChosenPublishDates(formattedData);
  //   updateInvite();
  // }
  function handleDeclineInvite(declineReason?: string) {
    if (declineReason) {
      setReason(declineReason);
    }
    declineInvite();
  }

  function handleShippingAddress(e: ChangeEvent<HTMLInputElement>) {
    setShippingAddress((prevState: TypeAny) => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  }
  function handleShippingAddressCountry(country: string | null) {
    setShippingAddress((prevState: TypeAny) => ({
      ...prevState,
      country
    }));
  }

  function handleChange(id: string, field: 'publish_date' | 'publish_time', value: TypeAny) {
    const val = field === 'publish_date' ? value : value.target.value;

    setRawChosenPublishDates((prevData) => {
      const beatIndex = prevData.findIndex((beat) => beat.beat_id === id);
      if (beatIndex > -1) {
        const updatedData = [...prevData];

        updatedData[beatIndex] = {
          ...updatedData[beatIndex],
          [field]: val || null
        };
        return updatedData;
      }
      return [
        ...prevData,
        {
          beat_id: id,
          publish_date: field === 'publish_date' ? val || null : null,
          publish_time: field === 'publish_time' ? val || '00:00' : '00:00',
          date_publish: null
        }
      ];
    });
  }

  function checkRequiredData(updatedUser?: Api.UserObject) {
    const userProfile = updatedUser || userData;
    const { missing_data } = userProfile;

    if (missing_data?.includes('NAME') || missing_data?.includes('PHONE')) {
      setMissingData(Step.UserInfo);
      return;
    }

    if (hasShipping && !checkShippingAddress) {
      if (missingAddress || missing_data?.includes('SHIPPING_ADDRESS')) {
        setMissingData(Step.ShippingAddress);
      } else {
        setMissingData(Step.CheckAddress);
      }
      setCheckShippingAddress(true);
      return;
    }

    if (hasMultipleDates) {
      setMissingData(Step.ChooseDate);
      return;
    }
    acceptInvite();
  }

  function updateUserData(newData: Api.UserObject) {
    updateUserInStorage(newData);
    setUserData(newData);
    setAuthData({ ...authData, user: newData });
    checkRequiredData(newData);
  }

  function renderStep() {
    switch (missingData) {
      case Step.Welcome:
        return (
          <CampaignWelcomeModal
            onNext={() =>
              router.push(fixUrlForCloudFront(`/connect/collaborations/${collaborationIdAsString}`))
            }
          />
        );
      case Step.UserInfo:
        return (
          <UserInfoModal
            userInfo={{
              first_name: user.first_name || '',
              last_name: user.last_name || '',
              phone: user.phone || ''
            }}
            onNext={updateUserData}
          />
        );
      case Step.CheckAddress:
        return (
          <CheckAddressModal
            data={user?.influencer?.shipping_address}
            onConfirm={() => checkRequiredData()}
            onChangeData={() => setMissingData(Step.ShippingAddress)}
          />
        );
      case Step.ShippingAddress:
        return (
          <AdressModal
            shippingAddress={shippingAddress}
            handleChange={handleShippingAddress}
            handleCountryChange={handleShippingAddressCountry}
            onNext={() => checkRequiredData()}
          />
        );
      case Step.Decline:
        return (
          <DeclineModal
            onDecline={handleDeclineInvite}
            onCancel={() => {
              setReason('');
              setMissingData(null);
            }}
            isLoading={isDeclining}
          />
        );
      case Step.ChooseDate:
        return (
          <ChooseDateModal
            data={memoData}
            selectedData={rawChosenPublishDates}
            onChange={handleChange}
            onSubmit={handleAcceptInvite}
            disabled={missingSelectedDate}
          />
        );
      default:
        return null;
    }
  }

  const getExpirationMessage = (dateExpire: Date) => {
    const expireDate = dayjs(dateExpire);
    const now = dayjs();
    const differenceInDays = expireDate.diff(now, 'day');

    if (differenceInDays < 0) {
      return <span>expired</span>;
    }
    if (differenceInDays === 0) {
      return <span className={styles.expireToday}>{t('collaborations.today')} 🔥</span>;
    }
    return (
      <span
        className={styles.expireFewDays}
      >{` ${differenceInDays + 1} ${t('collaborations.days')}`}</span>
    );
  };

  const handleCopy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(link);
      message.success(t('alert.COPIED'));
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      console.error('copy error', error);
    }
  };

  if (!['INVITED', 'INVITE_DECLINED', 'INVITE_EXPIRED'].includes(memoData.status)) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <div style={{ flexGrow: 1 }}>
        <TranslatedLink href="/connect/collaborations">
          <div className={styles.linkBack}>
            <ChevronLeft />
            <span>{t('collaborations.allCampaigns')}</span>
          </div>
        </TranslatedLink>
        <div className={styles.title}>{memoData.campaign.campaign_name}</div>
        <div className={styles.brand}>
          {memoData.campaign.brand_logo_url && (
            <img className={styles.brandLogo} src={memoData.campaign.brand_logo_url} alt="" />
          )}
          <div className={styles.brandName}>{memoData.campaign.brand_name}</div>
        </div>
        <div className={styles.body}>
          <div className={styles.content}>
            <div className={styles.welcomeText}>{t('inviteView.welcome')}</div>

            {memoData.notes && (
              <div className={styles.note}>
                <div className={styles.noteTitle}>{t('inviteView.note')}</div>
                <div
                  className={styles.noteBody}
                  dangerouslySetInnerHTML={{ __html: memoData.notes }}
                />
              </div>
            )}

            {(memoData.campaign.brief?.description?.trim() ||
              !!memoData.campaign.brief?.files?.length) && (
              <ExpandableBox
                title={<div className={styles.briefLabel}>{t('inviteView.campaignBrief')}</div>}
              >
                {!!memoData.campaign.brief?.files?.length && (
                  <div className={styles.files}>
                    {memoData.campaign.brief?.files?.map((file) => (
                      <Link key={file.url} href={file.url} target="_blank">
                        <div className={styles.file}>
                          {getFileIcon(file.filename)}
                          {file.filename}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
                {memoData.campaign.brief?.description?.trim() && (
                  <BriefViewer html={memoData.campaign.brief.description} />
                )}
              </ExpandableBox>
            )}

            {memoData.beats.map((beat, i) => {
              const products = beat.products
                ? Object.entries(beat.products).map(([key, value]) => ({
                    key,
                    value
                  }))
                : [];
              return (
                <ExpandableBox
                  key={beat.beat_id}
                  title={
                    <div className={styles.briefLabel}>
                      <span>
                        {t('inviteView.beat')} {i + 1}
                      </span>
                      <span className={styles.beatLabel}>{beat.beat_name}</span>
                    </div>
                  }
                >
                  <div className={styles.beatInfo}>
                    <div className={styles.beatInfoRow}>
                      <div className={styles.beatInfoLabel}>{t('inviteView.task')}:</div>
                      <div className={styles.beatInfoContentFlex}>
                        {PlatformIcon(beat.platform)}
                        {products.map(({ key, value }) => (
                          <div key={key} className={styles.platformItem}>
                            <span>{value}</span> {key}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className={styles.beatInfoRow}>
                      <div className={styles.beatInfoLabel}>{t('inviteView.draftsDate')}:</div>
                      <div className={styles.beatInfoContent}>
                        {dayjs(beat.date_draft).format('YYYY-MM-DD')}
                      </div>
                    </div>
                    {beat.dates_publish && (
                      <div className={styles.beatInfoRow}>
                        <div className={styles.beatInfoLabel}>
                          {t('inviteView.publicationDate')}:
                        </div>
                        <div className={styles.beatInfoContent}>
                          {beat.dates_publish.map((date, index) => (
                            <span key={date}>
                              {dayjs(date).format('DD.MM.YYYY')}
                              {index < beat.dates_publish.length - 1 && (
                                <span className={styles.beatInfoContentSpan}>
                                  {` ${t('inviteView.or')} `}
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {!!beat.brief?.files?.length && (
                    <div className={styles.files}>
                      {beat.brief?.files?.map((file) => (
                        <Link key={file.url} href={file.url} target="_blank">
                          <div className={styles.file}>
                            {getFileIcon(file.filename)}
                            {file.filename}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                  {beat.brief?.description?.trim() && <BriefViewer html={beat.brief.description} />}
                </ExpandableBox>
              );
            })}

            {!!memoData.trackers.length && (
              <>
                <div className={styles.links}>{t('inviteView.trackingUrl')}</div>
                <div className={styles.briefLinks}>
                  {memoData.trackers.map((tracker) => (
                    <div key={tracker.tracker_url} className={styles.briefLinkItem}>
                      <a
                        href={tracker.tracker_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.briefLink}
                      >
                        {tracker.tracker_url}
                      </a>
                      <div className={styles.briefLinkIcon}>
                        <button
                          type="button"
                          onClick={() => handleCopy(tracker.tracker_url)}
                          className={styles.briefLinkBtn}
                        >
                          <Copy
                            size={20}
                            color={copiedLink === tracker.tracker_url ? 'green' : '#474e54'}
                          />
                        </button>
                        {tracker.tracker_note && (
                          <span style={{ fontSize: '14px', color: '#555' }}>
                            {tracker.tracker_note}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className={styles.sticky}>
            <div className={styles.stickyContent}>
              {memoData.status === 'INVITED' && (
                <div className={styles.stickyRow}>
                  <div className={styles.stickyLabel}>{t('collaborations.expiredIn')}:</div>
                  <div className={styles.stickyExpires}>
                    {getExpirationMessage(memoData.date_expire)}
                    <Tooltip
                      title={
                        <Trans
                          i18nKey="collaborations.expireMsg"
                          values={{
                            days: dayjs(memoData.date_expire).diff(dayjs(), 'day')
                              ? dayjs(memoData.date_expire).diff(dayjs(), 'day') + 1
                              : dayjs(memoData.date_expire).diff(dayjs(), 'day')
                          }}
                          components={{
                            1: <span />
                          }}
                        />
                      }
                    >
                      <Info size={14} color="#474E5450" />
                    </Tooltip>
                  </div>
                </div>
              )}
              {memoData.status === 'INVITE_EXPIRED' && (
                <div className={`${styles.stickyRowExpired} ${styles.textRed}`}>
                  <IconExpired />
                  <span>{t('collaborations.inviteExpired')}</span>
                </div>
              )}
              {memoData.status === 'INVITE_DECLINED' && (
                <>
                  <div className={styles.stickyRowExpired}>
                    <IconDeclined />
                    <span>{t('collaborations.inviteDeclined')}</span>
                  </div>
                  {memoData.invite_feedback && (
                    <div className={`${styles.stickyRow} ${styles.stickyRowMsg}`}>
                      <IconNote />
                      <span>{memoData.invite_feedback}</span>
                    </div>
                  )}
                </>
              )}
              <div className={styles.stickyRow}>
                <div className={styles.stickyLabel}>{t('campaignNewBrief.campaignBudget')}:</div>
                <div className={styles.stickyLabel}>
                  <span className={styles.stickyBold}>{memoData.amount}</span> {memoData.currency}
                </div>
              </div>
              <Checkbox
                className={`${styles.terms} ${memoData.status !== 'INVITED' ? styles.disabled : ''}`}
                disabled={memoData.status !== 'INVITED'}
                onChange={confirmTerms}
              >
                <Trans
                  i18nKey="inviteView.agreement"
                  components={{
                    1: <TranslatedLink href={AGREEMENT_URL} key="agreement-link" target="_blank" />
                  }}
                />
              </Checkbox>
            </div>
            <Divider style={{ margin: 0 }} />
            <div className={`${styles.stickyContent} ${styles.flexEnd}`}>
              <Button
                disabled={memoData.status !== 'INVITED'}
                onClick={() => setMissingData(Step.Decline)}
              >
                {t('inviteView.decline')}
              </Button>
              <Button
                theme="cta"
                disabled={!terms || memoData.status !== 'INVITED'}
                isLoading={isAccepting}
                onClick={() => checkRequiredData()}
              >
                {t('inviteView.acceptInvite')}
              </Button>
            </div>
            <div className={`${styles.stickyContent} ${styles.stickyContentSm}`}>
              {memoData.status === 'INVITED' && (
                <Checkbox
                  className={`${styles.terms} ${memoData.status !== 'INVITED' ? styles.disabled : ''}`}
                  disabled={memoData.status !== 'INVITED'}
                  onChange={confirmTerms}
                  style={{ marginBottom: '12px' }}
                >
                  <Trans
                    i18nKey="inviteView.agreement"
                    components={{
                      1: (
                        <TranslatedLink href={AGREEMENT_URL} key="agreement-link" target="_blank" />
                      )
                    }}
                  />
                </Checkbox>
              )}
              <div className={`${styles.stickyRow} ${styles.noSpacing}`}>
                <div className={styles.stickyLabel}>
                  <div>
                    {t('campaignNewBrief.campaignBudget')}:
                    <span className={styles.stickyBold}> {memoData.amount}</span>{' '}
                    {memoData.currency}
                  </div>
                  {memoData.status === 'INVITED' && (
                    <div className={styles.flex}>
                      {t('collaborations.expireIn')}: {getExpirationMessage(memoData.date_expire)}
                      <Tooltip
                        title={
                          <Trans
                            i18nKey="collaborations.expireMsg"
                            values={{
                              days: dayjs(memoData.date_expire).diff(dayjs(), 'day')
                                ? dayjs(memoData.date_expire).diff(dayjs(), 'day') + 1
                                : dayjs(memoData.date_expire).diff(dayjs(), 'day')
                            }}
                            components={{
                              1: <span />
                            }}
                          />
                        }
                      >
                        <Info size={14} color="#474E5450" />
                      </Tooltip>
                    </div>
                  )}
                </div>
                <div className={styles.stickyLabel}>
                  {memoData.status === 'INVITE_EXPIRED' && (
                    <div
                      className={`${styles.stickyRowExpired} ${styles.noSpacing} ${styles.textRed}`}
                    >
                      <IconExpired />
                      <span>{t('collaborations.inviteExpired')}</span>
                    </div>
                  )}
                  {memoData.status === 'INVITE_DECLINED' && (
                    <div className={`${styles.stickyRowExpired} ${styles.noSpacing}`}>
                      <IconDeclined />
                      <span>{t('collaborations.inviteDeclined')}</span>
                    </div>
                  )}
                  {memoData.status === 'INVITED' && (
                    <div className={styles.buttons}>
                      <Button
                        disabled={memoData.status !== 'INVITED'}
                        onClick={() => setMissingData(Step.Decline)}
                      >
                        {t('inviteView.decline')}
                      </Button>
                      <Button
                        theme="cta"
                        disabled={!terms || memoData.status !== 'INVITED'}
                        onClick={() => checkRequiredData()}
                      >
                        {t('campaignPageDrafts.accept')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <Footer />
      </div>
      {renderStep()}
    </div>
  );
}
